import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Upload,
  Search,
  Trash2,
  Archive,
  RefreshCw,
  AlertCircle,
  LayoutGrid,
  List,
  Pencil,
  MoreHorizontal,
  FolderOpen,
  ExternalLink,
  ArchiveRestore,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API_BASE_URL } from '@/config';
import { projectApi } from '@/api/projectApi';
import { getAuthHeaders } from '@/api/workosAuth';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { usePostHog } from '@posthog/react';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import type { Project } from '@/types/project';

// Project detail page at /app/projects/:projectId includes:
// - Project overview health: ProjectStatusStrip (schedule health, critical path, milestones, data quality, baseline)
// - Recent snapshots: "Snapshot history" section with compare link to /app/project-state/:projectId/change-intelligence when 2+ snapshots exist

/* ─── Helpers ─────────────────────────────────────────── */

/** Human-friendly relative time (e.g. "3d ago", "2w ago") */
function timeAgo(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/* ─── Types ───────────────────────────────────────────── */

type ViewMode = 'list' | 'grid';
type StatusFilter = 'active' | 'archived';
type LastUpdatedFilter = 'any' | '7d' | '30d';
type BaselineFilter = 'any' | 'yes' | 'no';

interface SnapshotMeta {
  latest_schedule?: { snapshot_id: string; uploaded_at: string; file_name: string };
  latest_baseline?: { snapshot_id: string; uploaded_at: string; file_name: string };
}

/* ─── Component ───────────────────────────────────────── */

export const ProjectsWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const { refreshProjects } = useGlobalProject();
  const { settings } = useSettings();
  const posthog = usePostHog();

  /* ── Core state ── */
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snapshotMetadata, setSnapshotMetadata] = useState<Record<string, SnapshotMeta>>({});

  /* ── Filters & view ── */
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [lastUpdatedFilter, setLastUpdatedFilter] = useState<LastUpdatedFilter>('any');
  const [baselineFilter, setBaselineFilter] = useState<BaselineFilter>('any');

  /** Inline error for single-action failures (archive/delete/etc.) — does NOT hide the project list */
  const [actionError, setActionError] = useState<string | null>(null);

  /* ── Import Update picker ── */
  const [showImportPicker, setShowImportPicker] = useState(false);
  const [importPickerSearch, setImportPickerSearch] = useState('');

  /* ── Modals (all existing, preserved) ── */
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [projectToArchive, setProjectToArchive] = useState<{ id: string; name: string } | null>(null);
  const [archivingProjectId, setArchivingProjectId] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [editName, setEditName] = useState('');
  const [editOrg, setEditOrg] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [updateFile, setUpdateFile] = useState<File | null>(null);
  const [baselineFile, setBaselineFile] = useState<File | null>(null);
  const [baselineName, setBaselineName] = useState('');
  const [uploadingUpdate, setUploadingUpdate] = useState(false);
  const [uploadingBaseline, setUploadingBaseline] = useState(false);
  const [uploadModalError, setUploadModalError] = useState<string | null>(null);

  /* ── Data fetching ── */

  useEffect(() => {
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchProjects recreated each render; statusFilter is key
  }, [statusFilter]);

  useEffect(() => {
    if (projects.length > 0) {
      projects.forEach((p) => fetchSnapshotMetadata(p.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSnapshotMetadata recreated each render; projects is key
  }, [projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectApi.getProjects({ status: statusFilter });
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchSnapshotMetadata = async (projectId: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/v1/snapshots/${projectId}/metadata`, {
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setSnapshotMetadata((prev) => ({ ...prev, [projectId]: data }));
      }
    } catch (err) {
      console.error('Error fetching snapshot metadata:', err);
    }
  };

  /* ── Handlers (all existing business logic preserved) ── */

  const handleDeleteClick = (projectId: string, projectName: string) => {
    setProjectToDelete({ id: projectId, name: projectName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    const { id: projectId } = projectToDelete;
    try {
      setDeletingProjectId(projectId);
      setShowDeleteModal(false);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to delete project');
      }
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      await fetchProjects();
      refreshProjects();
      setProjectToDelete(null);
    } catch (err) {
      console.error('Error deleting project:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleArchiveClick = (projectId: string, projectName: string) => {
    setProjectToArchive({ id: projectId, name: projectName });
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async () => {
    if (!projectToArchive) return;
    const { id: projectId } = projectToArchive;
    try {
      setArchivingProjectId(projectId);
      setShowArchiveModal(false);
      setActionError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/archive`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.message || 'Failed to archive project');
      }
      // Optimistically remove from current list, then refetch
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      refreshProjects();
      setProjectToArchive(null);
    } catch (err) {
      console.error('Error archiving project:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to archive project');
    } finally {
      setArchivingProjectId(null);
    }
  };

  const handleArchiveCancel = () => {
    setShowArchiveModal(false);
    setProjectToArchive(null);
  };

  /** Unarchive: restore project to active status */
  const handleUnarchiveConfirm = async (projectId: string) => {
    try {
      setActionError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/unarchive`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.message || 'Failed to unarchive project');
      }
      // Optimistically remove from archived list, then refetch
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      refreshProjects();
    } catch (err) {
      console.error('Error unarchiving project:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to unarchive project');
    }
  };

  const handleEditClick = (project: Project) => {
    setProjectToEdit(project);
    setEditName(project.name || '');
    setEditOrg(project.organization || '');
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!projectToEdit) return;
    setSavingEdit(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${projectToEdit.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: editName.trim(),
          display_organization: editOrg.trim(),
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.message || 'Failed to update project');
      }
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectToEdit.id
            ? {
                ...p,
                name: editName.trim() || p.name,
                organization: editOrg.trim() || p.organization,
              }
            : p
        )
      );
      refreshProjects();
      setShowEditModal(false);
      setProjectToEdit(null);
    } catch (err) {
      console.error('Error updating project:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setProjectToEdit(null);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedProject || !updateFile) return;
    setUploadModalError(null);
    setUploadingUpdate(true);
    posthog?.capture('upload_started', { project_id: selectedProject.id, type: 'update' });
    try {
      await projectApi.uploadXerNewProject(selectedProject.name, updateFile, {
        projectId: selectedProject.id,
      });
      setShowUpdateModal(false);
      setSelectedProject(null);
      setUpdateFile(null);
      await fetchProjects();
      refreshProjects();
    } catch (err) {
      setUploadModalError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingUpdate(false);
    }
  };

  const handleBaselineSubmit = async () => {
    if (!selectedProject || !baselineFile) return;
    if (!baselineName.trim()) {
      setUploadModalError('Give this baseline a name.');
      return;
    }
    setUploadModalError(null);
    setUploadingBaseline(true);
    posthog?.capture('upload_started', { project_id: selectedProject.id, type: 'baseline' });
    try {
      await projectApi.uploadXerNewProject(selectedProject.name, baselineFile, {
        projectId: selectedProject.id,
        isBaseline: true,
        description: baselineName.trim(),
      });
      setShowBaselineModal(false);
      setSelectedProject(null);
      setBaselineFile(null);
      setBaselineName('');
      await fetchProjects();
      refreshProjects();
    } catch (err) {
      setUploadModalError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingBaseline(false);
    }
  };

  /* ── Import picker handler ── */
  const handleImportPickerSelect = (project: Project) => {
    setShowImportPicker(false);
    setImportPickerSearch('');
    setSelectedProject(project);
    setShowUpdateModal(true);
  };

  /* ── Derived data ── */

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const da = new Date(a.updated_at || a.created_at || 0).getTime();
      const db = new Date(b.updated_at || b.created_at || 0).getTime();
      return db - da;
    });
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return sortedProjects.filter((project) => {
      // Search
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matchesSearch =
          project.name.toLowerCase().includes(q) ||
          project.organization?.toLowerCase().includes(q) ||
          project.description?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      // Last updated
      if (lastUpdatedFilter !== 'any') {
        const dateStr = project.updated_at || project.created_at;
        if (dateStr) {
          const diffDays = (Date.now() - new Date(dateStr).getTime()) / 86_400_000;
          if (lastUpdatedFilter === '7d' && diffDays > 7) return false;
          if (lastUpdatedFilter === '30d' && diffDays > 30) return false;
        }
      }
      // Baseline
      if (baselineFilter !== 'any') {
        const hasBaseline = !!snapshotMetadata[project.id]?.latest_baseline;
        if (baselineFilter === 'yes' && !hasBaseline) return false;
        if (baselineFilter === 'no' && hasBaseline) return false;
      }
      return true;
    });
  }, [sortedProjects, searchTerm, lastUpdatedFilter, baselineFilter, snapshotMetadata]);

  const importPickerProjects = useMemo(() => {
    if (!importPickerSearch.trim()) return sortedProjects;
    const q = importPickerSearch.toLowerCase();
    return sortedProjects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.organization?.toLowerCase().includes(q)
    );
  }, [sortedProjects, importPickerSearch]);

  const hasActiveFilters =
    searchTerm !== '' || lastUpdatedFilter !== 'any' || baselineFilter !== 'any';

  const clearFilters = () => {
    setSearchTerm('');
    setLastUpdatedFilter('any');
    setBaselineFilter('any');
  };

  /* ── Render helpers ── */

  /** Updated date for a project */
  const getUpdatedDate = (project: Project) =>
    project.updated_at || project.created_at || project.uploaded_at;

  /** Baseline status text */
  const getBaselineLabel = (projectId: string) =>
    snapshotMetadata[projectId]?.latest_baseline ? 'Set' : 'Not set';

  /** Kebab dropdown menu for a single project — different actions for active vs archived */
  const renderKebab = (project: Project, isArchived = false) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {isArchived ? (
          /* Archived project actions */
          <>
            <DropdownMenuItem onClick={() => handleUnarchiveConfirm(project.id)}>
              <ArchiveRestore className="h-3.5 w-3.5 mr-2" />
              Unarchive
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleDeleteClick(project.id, project.name)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete
            </DropdownMenuItem>
          </>
        ) : (
          /* Active project actions */
          <>
            <DropdownMenuItem
              onClick={() => {
                setSelectedProject(project);
                setShowUpdateModal(true);
              }}
            >
              <Upload className="h-3.5 w-3.5 mr-2" />
              Import update
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSelectedProject(project);
                setShowBaselineModal(true);
              }}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
              Upload baseline
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/app/baselines')}>
              <ExternalLink className="h-3.5 w-3.5 mr-2" />
              Manage baselines
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleEditClick(project)}>
              <Pencil className="h-3.5 w-3.5 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleArchiveClick(project.id, project.name)}>
              <Archive className="h-3.5 w-3.5 mr-2" />
              Archive
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDeleteClick(project.id, project.name)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  /* ──────────────────── RENDER ──────────────────── */

  return (
    <div className="flex flex-col h-full">
      {/* ──── Header ──── */}
      <header className="border-b border-border px-4 py-3 bg-card/50">
        <PageHeader
          title="Projects"
          subtitle={
            loading
              ? undefined
              : statusFilter === 'archived'
                ? `Archived · ${projects.length} project${projects.length !== 1 ? 's' : ''}`
                : `Portfolio overview · ${projects.length} project${projects.length !== 1 ? 's' : ''}`
          }
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportPicker(true)}
                disabled={projects.length === 0}
                title={
                  projects.length === 0
                    ? 'Requires at least one existing project'
                    : 'Import a schedule update into an existing project'
                }
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Import Update
              </Button>
              <Button size="sm" onClick={() => navigate('/app/upload')}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                New Project
              </Button>
            </div>
          }
        />

        {/* Filter bar */}
        <div className="flex items-center gap-2 mt-2">
          {/* Active / Archived toggle */}
          <div className="flex rounded-md border border-border overflow-hidden mr-1">
            <button
              type="button"
              onClick={() => setStatusFilter('active')}
              className={cn(
                'px-2.5 py-1 text-xs font-medium',
                statusFilter === 'active'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('archived')}
              className={cn(
                'px-2.5 py-1 text-xs font-medium',
                statusFilter === 'archived'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
            >
              Archived
            </button>
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search projects…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>

          <Select
            value={lastUpdatedFilter}
            onValueChange={(v) => setLastUpdatedFilter(v as LastUpdatedFilter)}
          >
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <SelectValue placeholder="Last updated" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any time</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={baselineFilter}
            onValueChange={(v) => setBaselineFilter(v as BaselineFilter)}
          >
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <SelectValue placeholder="Baseline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any baseline</SelectItem>
              <SelectItem value="yes">Has baseline</SelectItem>
              <SelectItem value="no">No baseline</SelectItem>
            </SelectContent>
          </Select>

          {/* Spacer */}
          <div className="flex-1" />

          {/* View toggle */}
          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={cn(
                'p-1.5',
                viewMode === 'list'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
              title="List view"
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-1.5',
                viewMode === 'grid'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
              title="Grid view"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ──── Content ──── */}
      <div className="flex-1 overflow-y-auto">
        {/* Inline action error (archive/delete/etc. — does NOT replace the project list) */}
        {actionError && (
          <div className="mx-4 mt-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2.5 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            <p className="text-xs text-destructive flex-1">{actionError}</p>
            <button
              type="button"
              onClick={() => setActionError(null)}
              className="text-destructive/60 hover:text-destructive text-xs font-medium shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="p-4">
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="border-b border-border bg-muted/30 px-4 py-2.5 flex items-center gap-8">
                {['w-20', 'w-24', 'w-20', 'w-16', 'w-12'].map((w, i) => (
                  <div key={i} className={cn('h-2.5 rounded bg-muted animate-pulse', w)} />
                ))}
              </div>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="border-b border-border last:border-0 px-4 py-3.5 flex items-center gap-8 animate-pulse"
                >
                  <div className="h-3.5 w-44 bg-muted rounded" />
                  <div className="h-3 w-28 bg-muted rounded" />
                  <div className="h-3 w-20 bg-muted rounded" />
                  <div className="h-3 w-14 bg-muted rounded" />
                  <div className="ml-auto h-3 w-16 bg-muted rounded" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="p-4">
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-destructive">
                    Failed to load projects
                  </h3>
                  <p className="text-xs text-destructive/80 mt-1">{error}</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchProjects} className="shrink-0">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Empty: no projects at all */}
        {!loading && !error && projects.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
            {statusFilter === 'archived' ? (
              <>
                <Archive className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-base font-semibold text-foreground mb-1">No archived projects</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  When you archive a project, it will appear here.
                </p>
                <Button variant="outline" onClick={() => setStatusFilter('active')}>
                  View active projects
                </Button>
              </>
            ) : (
              <>
                <FolderOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-base font-semibold text-foreground mb-1">No projects yet</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Create your first project by importing a Primavera P6 XER file.
                  <br />
                  <span className="text-xs">
                    A project is a container for your schedule snapshots and baselines.
                  </span>
                </p>
                <div className="flex items-center gap-3">
                  <Button onClick={() => navigate('/app/upload')}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Project
                  </Button>
                  <Button variant="outline" disabled title="Requires an existing project">
                    <Upload className="h-4 w-4 mr-2" />
                    Import Update
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty: filters produced no results */}
        {!loading && !error && projects.length > 0 && filteredProjects.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <Search className="h-8 w-8 text-muted-foreground/50 mb-3" />
            <h3 className="text-sm font-medium text-foreground mb-1">No matching projects</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Try adjusting your search or filters.
            </p>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* ──── LIST VIEW ──── */}
        {!loading && !error && filteredProjects.length > 0 && viewMode === 'list' && (
          <div className="p-4">
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Project
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">
                      Organization
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">
                      Last Updated
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell">
                      Latest Snapshot
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell">
                      Baseline
                    </th>
                    <th className="px-4 py-2 w-[100px]">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredProjects.map((project) => {
                    const updatedDate = getUpdatedDate(project);
                    const meta = snapshotMetadata[project.id];
                    const latestSnapshotDate = meta?.latest_schedule?.uploaded_at;
                    const hasBaseline = !!meta?.latest_baseline;

                    return (
                      <tr
                        key={project.id}
                        onClick={() => navigate(`/app/projects/${project.id}`)}
                        className="transition-colors hover:bg-muted/50 cursor-pointer group"
                      >
                        {/* Project name */}
                        <td className="px-4 py-2.5">
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                              {project.name}
                            </span>
                            {/* Show org inline on small screens where the org column is hidden */}
                            {project.organization && (
                              <p className="text-[11px] text-muted-foreground mt-0.5 md:hidden truncate">
                                {project.organization}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Organization */}
                        <td className="px-4 py-2.5 hidden md:table-cell">
                          <span className="text-xs text-muted-foreground truncate block max-w-[200px]">
                            {project.organization || '—'}
                          </span>
                        </td>

                        {/* Last Updated */}
                        <td className="px-4 py-2.5 hidden sm:table-cell">
                          <span
                            className="text-xs text-muted-foreground"
                            title={
                              updatedDate
                                ? formatDateByPreference(updatedDate, settings.dateFormat, {
                                    timeZone: settings.timezone,
                                  })
                                : undefined
                            }
                          >
                            {timeAgo(updatedDate)}
                          </span>
                        </td>

                        {/* Latest Snapshot */}
                        <td className="px-4 py-2.5 hidden lg:table-cell">
                          <span
                            className="text-xs text-muted-foreground"
                            title={
                              latestSnapshotDate
                                ? formatDateByPreference(latestSnapshotDate, settings.dateFormat, {
                                    timeZone: settings.timezone,
                                  })
                                : undefined
                            }
                          >
                            {timeAgo(latestSnapshotDate)}
                          </span>
                        </td>

                        {/* Baseline */}
                        <td className="px-4 py-2.5 hidden lg:table-cell">
                          <Badge variant={hasBaseline ? 'success' : 'outline'} size="sm">
                            {hasBaseline ? 'Set' : 'Not set'}
                          </Badge>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-2.5">
                          <div
                            className="flex items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {statusFilter === 'archived' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2.5 text-xs"
                                onClick={() => handleUnarchiveConfirm(project.id)}
                              >
                                <ArchiveRestore className="h-3 w-3 mr-1" />
                                Unarchive
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2.5 text-xs"
                                onClick={() => navigate(`/app/projects/${project.id}`)}
                              >
                                Open
                              </Button>
                            )}
                            {renderKebab(project, statusFilter === 'archived')}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Count footer */}
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              Showing {filteredProjects.length} of {projects.length} project
              {projects.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* ──── GRID VIEW ──── */}
        {!loading && !error && filteredProjects.length > 0 && viewMode === 'grid' && (
          <div className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredProjects.map((project) => {
                const updatedDate = getUpdatedDate(project);
                const meta = snapshotMetadata[project.id];
                const hasBaseline = !!meta?.latest_baseline;

                return (
                  <div
                    key={project.id}
                    className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-muted-foreground/30 group flex flex-col"
                  >
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0 flex-1">
                        <h3
                          className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors cursor-pointer leading-tight"
                          onClick={() => navigate(`/app/projects/${project.id}`)}
                        >
                          {project.name}
                        </h3>
                        {project.organization && (
                          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                            {project.organization}
                          </p>
                        )}
                      </div>
                      {renderKebab(project, statusFilter === 'archived')}
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground mb-4">
                      {updatedDate && (
                        <span
                          title={formatDateByPreference(updatedDate, settings.dateFormat, {
                            timeZone: settings.timezone,
                          })}
                        >
                          Updated {timeAgo(updatedDate)}
                        </span>
                      )}
                      <Badge variant={hasBaseline ? 'success' : 'outline'} size="sm">
                        {hasBaseline ? 'Baseline set' : 'No baseline'}
                      </Badge>
                    </div>

                    {/* Primary action */}
                    <div className="mt-auto">
                      {statusFilter === 'archived' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => handleUnarchiveConfirm(project.id)}
                        >
                          <ArchiveRestore className="h-3 w-3 mr-1.5" />
                          Unarchive
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => navigate(`/app/projects/${project.id}`)}
                        >
                          Open
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              Showing {filteredProjects.length} of {projects.length} project
              {projects.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* ──── MODALS ──── */}

      {/* Import Update — project picker */}
      <Dialog
        open={showImportPicker}
        onOpenChange={(v) => {
          setShowImportPicker(v);
          if (!v) setImportPickerSearch('');
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Update</DialogTitle>
            <DialogDescription>
              Select a project to import a schedule update into. This creates a new snapshot for the
              selected project.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects…"
              value={importPickerSearch}
              onChange={(e) => setImportPickerSearch(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto -mx-2">
            {importPickerProjects.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {importPickerSearch
                  ? 'No projects match your search.'
                  : 'No projects available.'}
              </p>
            ) : (
              <div className="space-y-0.5">
                {importPickerProjects.map((project) => (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => handleImportPickerSelect(project)}
                    className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors rounded-md flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-foreground block truncate">
                        {project.name}
                      </span>
                      {project.organization && (
                        <span className="text-[11px] text-muted-foreground block truncate">
                          {project.organization}
                        </span>
                      )}
                    </div>
                    <Badge variant="outline" size="sm" className="shrink-0">
                      {getBaselineLabel(project.id)}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Update schedule modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update schedule</DialogTitle>
            <DialogDescription>
              Upload a new XER file to update the working schedule for{' '}
              <strong>{selectedProject?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {uploadModalError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {uploadModalError}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">XER file</label>
              <Input
                type="file"
                accept=".xer"
                onChange={(e) => {
                  setUpdateFile(e.target.files?.[0] || null);
                  setUploadModalError(null);
                }}
                className="bg-background"
              />
              {updateFile && (
                <p className="text-xs text-muted-foreground">
                  {updateFile.name} ({(updateFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowUpdateModal(false);
                setSelectedProject(null);
                setUpdateFile(null);
                setUploadModalError(null);
              }}
              disabled={uploadingUpdate}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateSubmit} disabled={!updateFile || uploadingUpdate}>
              {uploadingUpdate ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add baseline modal */}
      <Dialog open={showBaselineModal} onOpenChange={setShowBaselineModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add baseline</DialogTitle>
            <DialogDescription>
              Upload a XER file and give it a name. This creates a static snapshot for comparison
              (P6 baseline).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {uploadModalError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {uploadModalError}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Baseline name *</label>
              <Input
                value={baselineName}
                onChange={(e) => {
                  setBaselineName(e.target.value);
                  setUploadModalError(null);
                }}
                placeholder="e.g. Contract baseline, March 2025"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">XER file</label>
              <Input
                type="file"
                accept=".xer"
                onChange={(e) => {
                  setBaselineFile(e.target.files?.[0] || null);
                  setUploadModalError(null);
                }}
                className="bg-background"
              />
              {baselineFile && (
                <p className="text-xs text-muted-foreground">
                  {baselineFile.name} ({(baselineFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowBaselineModal(false);
                setSelectedProject(null);
                setBaselineFile(null);
                setBaselineName('');
                setUploadModalError(null);
              }}
              disabled={uploadingBaseline}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBaselineSubmit}
              disabled={!baselineFile || !baselineName.trim() || uploadingBaseline}
            >
              {uploadingBaseline ? 'Uploading…' : 'Upload baseline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / Rename Project Dialog */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename project</DialogTitle>
            <DialogDescription>
              Change the project name and organization shown in the app.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Project name</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Project name"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Organization</label>
              <Input
                value={editOrg}
                onChange={(e) => setEditOrg(e.target.value)}
                placeholder="Organization"
                className="bg-background"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleEditCancel} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={savingEdit || !editName.trim()}>
              {savingEdit ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving&hellip;
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive project</DialogTitle>
            <DialogDescription>
              Archive <strong>&ldquo;{projectToArchive?.name}&rdquo;</strong>? Archived projects are
              hidden from the active list. You can restore them later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleArchiveCancel}
              disabled={archivingProjectId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleArchiveConfirm}
              disabled={archivingProjectId !== null || !projectToArchive}
            >
              {archivingProjectId ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Archiving&hellip;
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  Archive
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>&ldquo;{projectToDelete?.name}&rdquo;</strong>?
              This action cannot be undone and will permanently delete all project data, including
              schedules, activities, and reports.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={deletingProjectId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingProjectId !== null || !projectToDelete}
            >
              {deletingProjectId ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting&hellip;
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Project
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
